import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance, { weburl } from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import { normalizeResumeMode } from "../../../helpers/jobSeekerResume";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
  { value: "blocked", label: "Blocked" },
];

const VERIFIED_OPTIONS = [
  { value: "", label: "All Verification" },
  { value: "1", label: "Verified" },
  { value: "0", label: "Unverified" },
];

const AdminUsersPage = ({
  role = "recruiter",
  title = "Recruiters",
  detailPath = "/user-recruiter",
}) => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1, limit: 10 });

  const getEditProfilePath = (user) => {
    const type = Number(user?.type);

    if (type === 2) {
      return `/admin/user/${user.id}/job-seeker-profile`;
    }

    if (type === 1) {
      return `/admin/user/${user.id}/vendor-profile`;
    }

    if (type === 3) {
      return `/admin/user/${user.id}/hospital-profile`;
    }

    return `${detailPath}/${user.id}`;
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const params = new URLSearchParams();
      params.set("role", role);
      params.set("page", String(page));
      params.set("limit", "10");
      if (searchTerm.trim()) params.set("search", searchTerm.trim());
      if (statusFilter) params.set("status", statusFilter);
      if (verifiedFilter) params.set("verified", verifiedFilter);

      const response = await axiosInstance.get(
        `/job-portal/admin/users?${params.toString()}`,
        authConfig
      );

      setUsers(response.data?.data?.users || []);
      setMeta(response.data?.meta || { total: 0, totalPages: 1, limit: 10 });
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [role, page, statusFilter, verifiedFilter, searchTerm]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
  };

  const updateUser = async (userId, payload) => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      await axiosInstance.put(
        `/job-portal/admin/users/${userId}/status`,
        payload,
        authConfig
      );
      toast.success("User updated");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update user");
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">{title}</h4>
              <p className="text-muted small mb-0">
                Admin control for {title.toLowerCase()}, moderation, and profile visibility.
              </p>
            </div>
            <div className="badge bg-light text-dark px-3 py-2">
              Total: {meta.total || 0}
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <form className="row g-3" onSubmit={handleSearchSubmit}>
                <div className="col-md-5">
                  <input
                    className="form-control"
                    placeholder={`Search ${title.toLowerCase()} by name, email, phone, or location`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.label} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={verifiedFilter}
                    onChange={(e) => {
                      setVerifiedFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    {VERIFIED_OPTIONS.map((option) => (
                      <option key={option.label} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100" type="submit">
                    Search
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Email / Phone</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Verification</th>
                    <th>{role === "recruiter" ? "Jobs / Applicants" : "Experience / Applications"}</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        Loading {title.toLowerCase()}...
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        No {title.toLowerCase()} found.
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="fw-semibold">{user.name || "-"}</div>
                          <div className="text-muted small">{user.companyName || user.role}</div>
                        </td>
                        <td>
                          <div>{user.email || "-"}</div>
                          <div className="text-muted small">{user.phone || "-"}</div>
                        </td>
                        <td>{[user.location?.city, user.location?.state].filter(Boolean).join(", ") || "-"}</td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={user.status || "1"}
                            onChange={(e) => updateUser(user.id, { status: e.target.value })}
                          >
                            {STATUS_OPTIONS.filter((option) => option.value).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={String(user.verified ?? 0)}
                            onChange={(e) =>
                              updateUser(user.id, { verified: Number(e.target.value) })
                            }
                          >
                            <option value="1">Verified</option>
                            <option value="0">Unverified</option>
                          </select>
                        </td>
                        <td>
                          {role === "recruiter" ? (
                            <>
                              <div>{user.counts?.totalJobs || 0} jobs</div>
                              <div className="text-muted small">
                                {user.counts?.totalReceivedApplications || 0} applicants
                              </div>
                            </>
                          ) : (
                            <>
                              <div>{user.experience ?? 0} years exp</div>
                              <div className="text-muted small">
                                {user.counts?.totalApplications || 0} applications
                              </div>
                            </>
                          )}
                        </td>
                        <td>
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString("en-IN")
                            : "-"}
                        </td>
                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            {(() => {
                              const resumeMode = normalizeResumeMode(
                                user.documents?.resumeMode || user.resumeMode,
                                Boolean(user.documents?.resume)
                              );
                              const uploadedResumeLink = user.documents?.resume
                                ? user.documents.resume.startsWith("public")
                                  ? `${weburl}${user.documents.resume.replace("public", "")}`
                                  : user.documents.resume
                                : "";

                              return (
                                <>
                            <Link
                              to={getEditProfilePath(user)}
                              className="btn btn-sm btn-outline-primary"
                            >
                              Edit
                            </Link>
                            {role === "recruiter" ? (
                              <Link to={`/all-job?recruiter=${user.id}`} className="btn btn-sm btn-outline-dark">
                                Jobs
                              </Link>
                            ) : null}
                            {role !== "recruiter" && resumeMode === "upload" && uploadedResumeLink ? (
                              <a
                                href={uploadedResumeLink}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-outline-secondary"
                              >
                                Resume
                              </a>
                            ) : null}
                            {role !== "recruiter" && (resumeMode === "auto" || !uploadedResumeLink) ? (
                              <Link
                                to={`${detailPath}/${user.id}#generated-resume`}
                                className="btn btn-sm btn-outline-secondary"
                              >
                                Generated Resume
                              </Link>
                            ) : null}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-body border-top d-flex justify-content-between align-items-center">
              <span className="text-muted small">
                Page {page} of {meta.totalPages || 1}
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page >= (meta.totalPages || 1)}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default AdminUsersPage;
